// src/orders/ExtendedDashboardBox.tsx
import { Box, Grid, Skeleton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Variant } from '@mui/material/styles/createTypography';
import { usePriceFormatter } from '../../common/usePriceFormatter';

type ExtendedDashboardBoxProps = {
  isLoading: boolean;
  title: string;
  today?: number;
  yesterday?: number;
  month?: number;
  isCurrency?: boolean;
  emphasized?: boolean;
};

export const ExtendedDashboardBox = ({
  isLoading,
  title,
  today,
  yesterday,
  month,

  isCurrency,
  emphasized = false,
}: ExtendedDashboardBoxProps) => {
  const { t } = useTranslation();
  const priceFormatter = usePriceFormatter();

  const formatValue = (value?: number, variant: Variant = 'h6') => {
    if (value === undefined) {
      return '0';
    }

    if (isCurrency) {
      return priceFormatter(value, false, variant);
    }
    return <Typography variant={variant}>{value.toLocaleString()}</Typography>;
  };

  return (
    <Box
      sx={{
        border: emphasized ? '2px solid' : '1px solid',
        borderColor: emphasized ? '#67AD5B' : '#CBCBCB',
        backgroundColor: emphasized ? 'primary.lighter' : 'background.paper',
        borderRadius: '8px',
        p: 2,
        minWidth: 250,
        boxShadow: emphasized ? 2 : 0,
        '& .MuiTypography-subtitle1': {
          color: emphasized ? '#67AD5B' : 'text.secondary',
          fontWeight: emphasized ? 700 : 400,
        },
        '& .MuiTypography-h4': {
          color: emphasized ? '#67AD5B' : 'inherit',
          fontWeight: emphasized ? 700 : undefined,
        },
      }}>
      <Grid container direction="column" gap={1}>
        <Typography variant="subtitle1" color="text.secondary">
          {title}
        </Typography>
        {isLoading ? (
          <Skeleton variant="rectangular" width={100} height={40} />
        ) : (
          <>
            {formatValue(today, 'h4')}
            <Grid container gap={0.5} flexWrap="nowrap">
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.yesterday')}:
              </Typography>
              {formatValue(yesterday, 'body2')}
              <Grid mx={1}>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
              </Grid>
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.thisMonth')}:
              </Typography>
              {formatValue(month, 'body2')}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};
